/**
 * Cross-Tenant Access Settings Component
 */

import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Title3,
  Body1,
  Text,
  Spinner,
  Button,
  shorthands,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableColumnDefinition,
  TableCellLayout,
  createTableColumn,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
} from '@fluentui/react-components';
import {
  MoreHorizontal20Regular,
  Delete20Regular,
  ArrowClockwise20Regular,
  Add20Regular,
  Edit20Regular,
} from '@fluentui/react-icons';
import { RoundedCard } from '../../common/RoundedCard';
import { Alert } from '../../common/Alert';
import { DataGridCard, DataGridToolbar, ToolbarButton } from '../../layouts';
import {
  useCrossTenantDefaultPolicy,
  useCrossTenantPartners,
} from '../../../hooks/useCrossTenantAccess';
import { OrganizationModal } from './EditOrganizationModal';
import type { OrganizationModalSettings } from './EditOrganizationModal';
import { CrossTenantSettingsPanel } from './CrossTenantSettingsPanel';
import type { CrossTenantPartner, B2BAccessType, InboundTrust } from '../../../types/crossTenantAccess';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXL),
  },
  card: {
    ...shorthands.padding(tokens.spacingVerticalXL),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  partnerCardHeader: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXL),
    paddingBottom: tokens.spacingVerticalXL,
  },
  // Simple DataGrid styling - white header/footer, no card outline for embedded use
  simpleDataGrid: {
    // Remove card outline and shadow
    '& .fui-Card': {
      boxShadow: 'none',
      ...shorthands.border('none'),
      backgroundColor: tokens.colorNeutralBackground1,
    },
    '& .fui-DataGridHeader': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    '& [role="columnheader"]': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    // Override pagination footer background (targets the last div inside the card)
    '& .fui-Card > div:last-child': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
});

// Column sizing for partners DataGrid
const columnSizingOptions = {
  displayName: {
    minWidth: 150,
    defaultWidth: 200,
  },
  inboundAccess: {
    minWidth: 130,
    defaultWidth: 160,
  },
  outboundAccess: {
    minWidth: 130,
    defaultWidth: 160,
  },
  actions: {
    minWidth: 80,
    defaultWidth: 100,
  },
};

/**
 * Format B2B access type for display.
 * null = "Inherited from default"
 * "allow" = "Allow"
 * "block" = "Block"
 */
function formatAccessType(value: B2BAccessType | null): string {
  if (value === null) {
    return 'Inherited from default';
  }
  return value === 'allow' ? 'Allow' : 'Block';
}

interface CrossTenantAccessSettingsProps {
  tenantId: string;
}

export const CrossTenantAccessSettings: React.FC<CrossTenantAccessSettingsProps> = ({
  tenantId,
}) => {
  const styles = useStyles();

  const {
    policy,
    isLoading: isLoadingPolicy,
    error: policyError,
    updatePolicy,
    isUpdating: isUpdatingPolicy,
  } = useCrossTenantDefaultPolicy(tenantId);

  const {
    partners,
    isLoading: isLoadingPartners,
    isFetching: isFetchingPartners,
    error: partnersError,
    createPartner,
    isCreating,
    updatePartner,
    isUpdating,
    deletePartner,
    isDeleting,
    refetch: refetchPartners,
  } = useCrossTenantPartners(tenantId);

  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<CrossTenantPartner | null>(null);
  const [selectedPartners, setSelectedPartners] = useState<Set<string | number>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Organization modal state (unified for add and edit)
  const [organizationModalOpen, setOrganizationModalOpen] = useState(false);
  const [partnerToEdit, setPartnerToEdit] = useState<CrossTenantPartner | null>(null);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  /**
   * Handle changes from the CrossTenantSettingsPanel for default settings.
   * Updates the default policy via the API.
   */
  const handleDefaultSettingsChange = async (settings: {
    b2bCollaboration: { inbound: B2BAccessType | null; outbound: B2BAccessType | null };
    inboundTrust: InboundTrust | null;
  }) => {
    try {
      await updatePolicy({
        b2bCollaboration: {
          inbound: settings.b2bCollaboration.inbound ?? 'allow',
          outbound: settings.b2bCollaboration.outbound ?? 'allow',
        },
        inboundTrust: settings.inboundTrust ?? undefined,
      });
      setSuccess('Default settings updated successfully');
    } catch {
      // Error handled by hook
    }
  };

  const handleDeletePartner = async () => {
    if (!partnerToDelete) return;
    try {
      await deletePartner(partnerToDelete.tenantId);
      setSuccess(`Organization "${partnerToDelete.displayName}" removed successfully`);
      setDeleteDialogOpen(false);
      setPartnerToDelete(null);
    } catch {
      // Error handled by hook
    }
  };

  const openDeleteDialog = (partner: CrossTenantPartner) => {
    setPartnerToDelete(partner);
    setDeleteDialogOpen(true);
  };

  const handleBulkDelete = async () => {
    const selectedIds = Array.from(selectedPartners);
    setBulkDeleting(true);

    try {
      for (const partnerId of selectedIds) {
        await deletePartner(String(partnerId));
      }
      setSuccess(`${selectedIds.length} organization(s) removed successfully`);
      setSelectedPartners(new Set());
    } catch {
      // Error handled by hook
    } finally {
      setBulkDeleting(false);
      setBulkDeleteDialogOpen(false);
    }
  };

  const openAddPartnerModal = () => {
    setPartnerToEdit(null);
    setOrganizationModalOpen(true);
  };

  const openEditPartnerModal = (partner: CrossTenantPartner) => {
    setPartnerToEdit(partner);
    setOrganizationModalOpen(true);
  };

  const handleOrganizationModalSave = async (settings: OrganizationModalSettings) => {
    try {
      if (partnerToEdit) {
        // Edit mode
        await updatePartner({
          partnerTenantId: partnerToEdit.tenantId,
          inboundTrust: settings.inboundTrust,
          b2bCollaboration: settings.b2bCollaboration,
        });
        setSuccess(`Organization "${partnerToEdit.displayName}" updated successfully`);
      } else {
        // Add mode
        await createPartner({
          tenantId: settings.tenantId!,
          inboundTrust: settings.inboundTrust ?? undefined,
          b2bCollaboration: settings.b2bCollaboration,
        });
        setSuccess('Organization added successfully');
      }
      setOrganizationModalOpen(false);
      setPartnerToEdit(null);
    } catch {
      // Error handled by hook
    }
  };

  // Convert undefined to empty array for DataGridCard
  const partnerList: CrossTenantPartner[] = partners ?? [];

  // Column definitions for partners DataGrid
  const columns: TableColumnDefinition<CrossTenantPartner>[] = [
    createTableColumn<CrossTenantPartner>({
      columnId: 'displayName',
      compare: (a, b) => a.displayName.localeCompare(b.displayName),
      renderHeaderCell: () => 'Name',
      renderCell: (partner) => (
        <TableCellLayout truncate title={`${partner.displayName} (${partner.defaultDomainName})`}>
          {partner.displayName}
        </TableCellLayout>
      ),
    }),
    createTableColumn<CrossTenantPartner>({
      columnId: 'inboundAccess',
      renderHeaderCell: () => 'Inbound access',
      renderCell: (partner) => {
        const value = formatAccessType(partner.b2bCollaboration.inbound);
        return (
          <TableCellLayout truncate title={value}>
            {value}
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<CrossTenantPartner>({
      columnId: 'outboundAccess',
      renderHeaderCell: () => 'Outbound access',
      renderCell: (partner) => {
        const value = formatAccessType(partner.b2bCollaboration.outbound);
        return (
          <TableCellLayout truncate title={value}>
            {value}
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<CrossTenantPartner>({
      columnId: 'actions',
      renderHeaderCell: () => 'Actions',
      renderCell: (partner) => (
        <TableCellLayout>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                appearance="subtle"
                icon={<MoreHorizontal20Regular />}
                aria-label="More options"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Edit20Regular />} onClick={() => openEditPartnerModal(partner)}>
                  Edit
                </MenuItem>
                <MenuItem icon={<Delete20Regular />} onClick={() => openDeleteDialog(partner)}>
                  Remove
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </TableCellLayout>
      ),
    }),
  ];

  if (isLoadingPolicy || isLoadingPartners) {
    return (
      <RoundedCard className={styles.card}>
        <div className={styles.cardContent}>
          <Title3>Cross-Tenant Access</Title3>
          <Spinner label="Loading cross-tenant access settings..." />
        </div>
      </RoundedCard>
    );
  }

  return (
    <div className={styles.container}>
      {success && <Alert intent="success">{success}</Alert>}
      {policyError && (
        <Alert intent="error" title="Error Loading Policy">
          {policyError.message}
        </Alert>
      )}
      {partnersError && (
        <Alert intent="error" title="Error Loading Organizations">
          {partnersError.message}
        </Alert>
      )}

      {/* Default Settings Card */}
      <RoundedCard className={styles.card}>
        <div className={styles.cardContent}>
          <Title3>Default Settings</Title3>
          <Body1>
            Configure default cross-tenant access settings that apply to all external organizations unless overridden by a partner-specific configuration.
          </Body1>

          {policy && (
            <CrossTenantSettingsPanel
              mode="default"
              b2bCollaboration={policy.b2bCollaboration}
              inboundTrust={policy.inboundTrust}
              onChange={handleDefaultSettingsChange}
              disabled={isUpdatingPolicy}
            />
          )}
        </div>
      </RoundedCard>

      {/* Partner Configurations Card */}
      <RoundedCard style={{ padding: 0, gap: 0 }}>
        {/* Card Header with Title and Description */}
        <div className={styles.partnerCardHeader}>
          <Title3>Organizational Settings</Title3>
          <Body1>
            Cross-tenant access settings configured for specific Microsoft Entra tenants. Any tenants not listed here will use the default settings.
          </Body1>
        </div>

        {/* DataGridCard (nested) */}
        <div className={styles.simpleDataGrid} style={{ padding: tokens.spacingHorizontalL, paddingTop: 0 }}>
          <DataGridCard
            items={partnerList}
            columns={columns}
            getRowId={(partner) => partner.tenantId}
            columnSizingOptions={columnSizingOptions}
            selectable
            selectedIds={selectedPartners}
            onSelectionChange={setSelectedPartners}
            toolbar={
              <DataGridToolbar>
                <DataGridToolbar.Left>
                  <ToolbarButton
                    icon={<ArrowClockwise20Regular />}
                    onClick={() => refetchPartners()}
                    loading={isFetchingPartners}
                    loadingText="Refreshing..."
                  >
                    Refresh
                  </ToolbarButton>
                </DataGridToolbar.Left>
                <DataGridToolbar.Right>
                  <ToolbarButton
                    icon={<Delete20Regular />}
                    disabled={selectedPartners.size === 0}
                    onClick={() => setBulkDeleteDialogOpen(true)}
                  >
                    Delete{selectedPartners.size > 0 ? ` (${selectedPartners.size})` : ''}
                  </ToolbarButton>
                  <ToolbarButton
                    appearance="primary"
                    icon={<Add20Regular />}
                    onClick={openAddPartnerModal}
                  >
                    Add organization
                  </ToolbarButton>
                </DataGridToolbar.Right>
              </DataGridToolbar>
            }
            pagination={{
              showing: partnerList.length,
              total: partnerList.length,
            }}
            loading={isLoadingPartners}
            emptyMessage="No organizations found. Add an organization to configure tenant-specific access settings."
          />
        </div>
      </RoundedCard>

      {/* Single Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(_, data) => setDeleteDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Remove Organization</DialogTitle>
            <DialogContent>
              {partnerToDelete && (
                <Text>
                  Are you sure you want to remove the organizational settings for{' '}
                  <strong>{partnerToDelete.displayName}</strong> ({partnerToDelete.defaultDomainName})?
                </Text>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleDeletePartner}
                disabled={isDeleting}
              >
                {isDeleting ? 'Removing...' : 'Remove'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onOpenChange={(_, data) => setBulkDeleteDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Remove Organizations</DialogTitle>
            <DialogContent>
              {bulkDeleting ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: tokens.spacingVerticalL,
                  padding: tokens.spacingVerticalXXL,
                }}>
                  <Spinner size="large" label="Removing organizations..." />
                  <Body1>Please wait while organizations are being removed.</Body1>
                </div>
              ) : (
                <>
                  <Body1>
                    Are you sure you want to remove {selectedPartners.size} organization(s)? This action cannot be undone.
                  </Body1>
                  <div style={{ marginTop: tokens.spacingVerticalM }}>
                    <Body1 style={{ fontWeight: 600 }}>Organizations to be removed:</Body1>
                    <ul style={{ marginTop: tokens.spacingVerticalS, marginBottom: 0 }}>
                      {Array.from(selectedPartners).map((partnerId) => {
                        const partner = partnerList.find((p) => p.tenantId === partnerId);
                        return partner ? (
                          <li key={String(partnerId)} style={{ marginBottom: tokens.spacingVerticalXS }}>
                            {partner.displayName} ({partner.defaultDomainName})
                          </li>
                        ) : null;
                      })}
                    </ul>
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => setBulkDeleteDialogOpen(false)}
                disabled={bulkDeleting}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
              >
                {bulkDeleting ? 'Removing...' : 'Remove'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Add/Edit Organization Modal */}
      <OrganizationModal
        open={organizationModalOpen}
        partner={partnerToEdit}
        onClose={() => {
          setOrganizationModalOpen(false);
          setPartnerToEdit(null);
        }}
        onSave={handleOrganizationModalSave}
        saving={isCreating || isUpdating}
      />
    </div>
  );
};
